import { Component, OnInit, effect } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NavigationStart, Router, RouterOutlet } from "@angular/router";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { EventMessage, EventType } from "@azure/msal-browser";
import { filter, Subject, takeUntil } from "rxjs";
import { LoaderComponent } from "./shared/components/loader/loader.component";
import { LoaderService } from "./core/services/loader.service";
import { SignalRService } from "./core/services/signal-r.service";
import { GenericResponseDto } from "./core/models/generic-response.dto";
import { ValidateLogin } from "./core/models/validate-login.dto";
import { UserManagementService } from "./core/services/user-management.service";
import { SharedService } from "./core/services/shared.service";
import { WindowResizeService } from "./core/services/window-resize.service";
import { SidebarService } from "./core/services/sidebar.service";

@Component({
  selector: "app-root",
  standalone: true,
  imports: [RouterOutlet, LoaderComponent, CommonModule],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit {
  title = "MyNadel";
  showLoader = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private authService: MsalService,
    private router: Router,
    private broadcastService: MsalBroadcastService,
    private loaderService: LoaderService,
    private _signalRService: SignalRService,
    private _userManagement: UserManagementService,
    private _sharedService: SharedService,
    private _resize: WindowResizeService,
    private _sidebarService: SidebarService
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (
          location.hash.includes("id_token") ??
          location.hash.includes("access_token")
        ) {
          window.close();
        }
      }
    });

    effect(() => {
      const show = this.loaderService.status$();
      this.showLoader = show;
      if (this._signalRService.roleChanged()) {
        this.validateLogin();
        this.IsInventoryAccess();
      }

      this._resize.windowResize$.subscribe(() => {
        const width = this._resize.width();
        this._sidebarService.setSidebarState(width < 1200);
      });
    });
  }

  ngOnInit(): void {
    this._resize.windowResize$
      .pipe(takeUntil(this._destroying$))
      .subscribe(width => {
        this._sidebarService.setSidebarState(width < 1200);
      });

    this.broadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.LOGIN_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        this.authService.handleRedirectObservable().subscribe({
          next: () => {
            localStorage.setItem("user", JSON.stringify(result.payload));
            const accounts = this.authService.instance.getAllAccounts();
            if (accounts.length > 0) {
              this.authService.instance.setActiveAccount(accounts[0]);
            }
          },
          error: (err: unknown) => {
            if (err instanceof Error) {
              console.error("app-main-ngOnInit", err.message, err.stack);
            } else {
              console.error("app-main-ngOnInit", err);
            }
          },
        });
      });
    this._signalRService.createConnection();
    this.initializeTheme();
  }

  validateLogin() {
    if (localStorage.hasOwnProperty("user")) {
      this.authService.handleRedirectObservable().subscribe(
        () => {
          this._userManagement.validateLogin().subscribe({
            next: (res: GenericResponseDto<ValidateLogin>) => {
              if (!res.data.isProfileValid) {
                this.authService.logoutRedirect({
                  postLogoutRedirectUri: "/",
                });
                // this.displayPopUpValue =
                //   this._sharedService.getDisplayMobilePopUpValue();
                localStorage.clear();
                // localStorage.setItem(
                //   'MobilePopUpDisplay',
                //   this.displayPopUpValue
                // );
              }
              this._userManagement.userRole.next(res.data);
            },
          });
        },
        err => {
          console.error("validateLogin-", err);
        }
      );
    }
  }

  initializeTheme() {
    const element = document.querySelector("html");
    const savedTheme = localStorage.getItem("theme") ?? "light";

    if (savedTheme === "dark") {
      element?.classList.add("my-app-dark");
    } else {
      element?.classList.remove("my-app-dark");
    }
  }

  IsInventoryAccess() {
    this._sharedService.isInventoryAccess().subscribe({
      next: res => {
        if (res.data.inventoryAccess) {
          this._userManagement.inventoryAccess = res.data.inventoryAccess;
        } else {
          this._userManagement.inventoryAccess = false;
        }

        this._userManagement.isInventoryAccessChange.next(
          this._userManagement.inventoryAccess
        );
      },
      error: err => {
        console.error(`IsInventoryAccess`, err);
      },
    });
  }
}
